import { useEffect, useRef, useState, useCallback } from "react";
import { TileType, GameStats, IProgressService } from "../types";
import { GAME_CONFIG, LEVEL_SETTINGS, STORAGE_KEY } from "../constants";
import { generatePath } from "../utils/generatePath";
import { useAudioEffects } from "../../../hooks/use-audio-effects";
import matchAudio from "../assets/audio/click-2.wav";
import missAudio from "../assets/audio/miss-click.wav";
import gameWinAudio from "../assets/audio/level-complete.wav";
import gameOverAudio from "../assets/audio/game-over.wav";

/* ----------------------------------------------------------------------------
 * LocalProgressService (kept identical behaviour)
 * ---------------------------------------------------------------------------- */
export const LocalProgressService: IProgressService = {
  async saveProgress(stats: GameStats) {
    if (!GAME_CONFIG.SAVE_PROGRESS) return;
    try {
      const minimal = {
        level: stats.level,
        points: stats.points,
        attemptsLeft: stats.attemptsLeft,
        missesLeft: stats.missesLeft,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ stats: minimal }));
    } catch {
      // intentionally ignore storage errors
    }
  },

  async loadProgress() {
    if (!GAME_CONFIG.SAVE_PROGRESS) return null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  },

  async clearProgress() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  },
};

/* ----------------------------------------------------------------------------
 * useMemoryGame (refactored for observability and stability)
 * - Logic mostly preserved, but simplified API per request:
 *   - removed `handleLevelComplete` export (internal level-complete state still exists)
 *   - onWin is called when a level is completed and receives a rich payload
 *   - onLose is called when the game ends (no attempts left)
 *   - when last configured level is completed, the game ends (no auto-reset to level 1)
 * - No gameplay rule changes (attempts/misses/points remain identical)
 * ---------------------------------------------------------------------------- */
export function useMemoryGame(opts?: {
  startLevel?: number;
  onWin?: (payload: {
    stats: GameStats;
    level: number;
    gridSize: number;
    path: number[];
    tiles: TileType[];
    areAllLevelsCompleted: boolean;
  }) => void;
  onLose?: (payload: {
    stats: GameStats;
    level: number;
    gridSize: number;
  }) => void;
}) {
  // --- derived start level (ensure >= 1) ---
  const startLevel = Math.max(1, Math.floor(opts?.startLevel ?? 1));

  // Return the configured settings for a given level.
  const levelConfig = (level: number) =>
    LEVEL_SETTINGS[level] ?? LEVEL_SETTINGS[Object.keys(LEVEL_SETTINGS).length];

  const maxLevel = Math.max(
    ...Object.keys(LEVEL_SETTINGS).map((k) => Number(k))
  );

  // --- hydration guard: prevent saving before we've loaded persisted state ---
  const hasHydratedRef = useRef(false);

  // --- primary state ---
  const [currentLevel, setCurrentLevel] = useState<number>(startLevel);
  const [gridSize, setGridSize] = useState<number>(
    levelConfig(startLevel).gridSize
  );

  const buildTiles = (size: number) =>
    Array.from({ length: size * size }, (_, i) => ({
      id: i,
      state: "hidden" as const,
    }));

  const [path, setPath] = useState<number[]>([]);
  const [tiles, setTiles] = useState<TileType[]>(() =>
    buildTiles(levelConfig(startLevel).gridSize)
  );

  const [stats, setStats] = useState<GameStats>(() => {
    const cfg = levelConfig(startLevel);
    return {
      points: 0,
      level: startLevel,
      attemptsLeft: cfg.attempts,
      missesLeft: cfg.maxMisses,
    };
  });

  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isGameOver, setIsGameOver] = useState(false);
  const [areAllLevelsCompleted, setAreAllLevelsCompleted] =
    useState<boolean>(false);
  const [isLevelComplete, setIsLevelComplete] = useState(false);

  // timers storage
  const timersRef = useRef<number[]>([]);

  const { playEffect } = useAudioEffects();

  // --- refs that mirror latest state so callbacks/getters always have fresh data ---
  const statsRef = useRef<GameStats>(stats);
  const tilesRef = useRef<TileType[]>(tiles);
  const pathRef = useRef<number[]>(path);
  const currentLevelRef = useRef<number>(currentLevel);

  const setPathAndRef = (nextPath: number[]) => {
    setPath(nextPath);
    pathRef.current = nextPath;
  };

  const setCurrentLevelAndRef = (l: number) => {
    setCurrentLevel(l);
    currentLevelRef.current = l;
  };

  /* ------------------------- lightweight event emitter (optional) ------------------------- */
  const listenersRef = useRef<Record<string, Set<Function>>>({});

  const on = useCallback((event: string, fn: Function) => {
    if (!listenersRef.current[event]) listenersRef.current[event] = new Set();
    listenersRef.current[event].add(fn);
    return () => listenersRef.current[event]?.delete(fn);
  }, []);

  const off = useCallback((event: string, fn: Function) => {
    listenersRef.current[event]?.delete(fn);
  }, []);

  const emit = useCallback((event: string, payload?: any) => {
    const set = listenersRef.current[event];
    if (!set) return;
    Array.from(set).forEach((fn) => {
      try {
        fn(payload);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("useMemoryGame event listener error:", e);
      }
    });
  }, []);

  /* ------------------------- helpers ------------------------- */
  const clearTimers = useCallback(() => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  }, []);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  /* ------------------------- start level (internal) ------------------------- */
  const startLevelInternal = useCallback(
    (level: number) => {
      clearTimers();

      setCurrentLevelAndRef(level);

      const cfg = levelConfig(level);

      const newPath = generatePath(cfg.gridSize, cfg.pathLength, false);

      setGridSize(cfg.gridSize);
      setPathAndRef(newPath);
      setTiles(buildTiles(cfg.gridSize));
      tilesRef.current = buildTiles(cfg.gridSize);

      setStats((s) => {
        const next = {
          ...s,
          level,
          attemptsLeft: s.attemptsLeft ?? cfg.attempts,
          missesLeft: cfg.maxMisses,
        };
        statsRef.current = next;
        return next;
      });

      setIsPreviewing(true);
      setIsRunning(false);

      setTiles((prev) =>
        prev.map((t) =>
          newPath.includes(t.id)
            ? { ...t, state: "preview" }
            : { ...t, state: "hidden" }
        )
      );
      tilesRef.current = tilesRef.current.map((t) =>
        newPath.includes(t.id)
          ? { ...t, state: "preview" }
          : { ...t, state: "hidden" }
      );

      const previewTimer = window.setTimeout(() => {
        setTiles((prev) => prev.map((t) => ({ ...t, state: "hidden" })));
        tilesRef.current = tilesRef.current.map((t) => ({
          ...t,
          state: "hidden",
        }));
        setIsPreviewing(false);
        setIsRunning(true);
        emit("previewEnd", { level });
      }, cfg.previewDuration);

      timersRef.current.push(previewTimer);
      emit("previewStart", { level, previewDuration: cfg.previewDuration });
    },
    [clearTimers, emit]
  );

  /* ------------------------- tile interaction ------------------------- */
  const handleLevelCompleteInternal = useCallback(() => {
    // set UI state to indicate level completion
    setIsLevelComplete(true);
    setIsRunning(false);
    playEffect(gameWinAudio);

    const areAllLevelsCompleted = currentLevelRef.current >= maxLevel;

    setAreAllLevelsCompleted(areAllLevelsCompleted);

    const payload = {
      stats: statsRef.current,
      level: currentLevelRef.current,
      gridSize,
      path: pathRef.current,
      tiles: tilesRef.current,
      areAllLevelsCompleted,
    };

    // call consumer onWin with rich payload
    opts?.onWin?.(payload);
    emit("levelComplete", payload);

    // if final level, mark game as finished
    if (currentLevelRef.current >= maxLevel) {
      setIsGameOver(true);
      emit("gameFinished", { stats: statsRef.current });
    }
  }, [emit, gridSize, maxLevel, playEffect, opts]);

  const clickTile = useCallback(
    (id: number) => {
      if (!isRunning || isPreviewing || isGameOver || isLevelComplete) return;

      const tile = tilesRef.current.find((t) => t.id === id);
      if (!tile || tile.state === "found") return;

      if (pathRef.current.includes(id)) {
        playEffect(matchAudio);

        setTiles((prev) =>
          prev.map((t) => (t.id === id ? { ...t, state: "found" } : t))
        );
        tilesRef.current = tilesRef.current.map((t) =>
          t.id === id ? { ...t, state: "found" } : t
        );

        setStats((s) => {
          const next = {
            ...s,
            points: s.points + GAME_CONFIG.POINTS_CORRECT,
          };
          statsRef.current = next;
          return next;
        });

        const foundCount = tilesRef.current.filter(
          (t) => t.state === "found"
        ).length;
        if (foundCount >= pathRef.current.length) {
          handleLevelCompleteInternal();
        }

        emit("click", { id, correct: true });
      } else {
        playEffect(missAudio);

        setTiles((prev) =>
          prev.map((t) => (t.id === id ? { ...t, state: "wrong" } : t))
        );
        tilesRef.current = tilesRef.current.map((t) =>
          t.id === id ? { ...t, state: "wrong" } : t
        );

        const t = window.setTimeout(() => {
          setStats((s) => {
            const newMisses = s.missesLeft - 1;

            if (newMisses <= 0) {
              if (s.attemptsLeft > 1) {
                // restart same level but reduce attempts
                startLevelInternal(s.level);
                const next = {
                  ...s,
                  attemptsLeft: s.attemptsLeft - 1,
                  missesLeft: levelConfig(s.level).maxMisses,
                };
                statsRef.current = next;
                return next;
              } else {
                // final lose condition
                setIsGameOver(true);
                setIsRunning(false);
                playEffect(gameOverAudio);

                const payload = {
                  stats: { ...s, attemptsLeft: 0, missesLeft: 0, level: 1 },
                  level: 1,
                  gridSize,
                };
                statsRef.current = payload.stats;
                opts?.onLose?.(payload);
                emit("lose", payload);

                const next = { ...s, attemptsLeft: 0, missesLeft: 0, level: 1 };
                statsRef.current = next;
                return next;
              }
            }

            const next = {
              ...s,
              missesLeft: newMisses,
              points: s.points + GAME_CONFIG.POINTS_WRONG,
            };
            statsRef.current = next;
            return next;
          });
        }, 80);

        timersRef.current.push(t);
        emit("click", { id, correct: false });
      }
    },
    [
      emit,
      gridSize,
      isGameOver,
      isLevelComplete,
      isPreviewing,
      isRunning,
      playEffect,
      startLevelInternal,
    ]
  );

  /* ------------------------- public API actions ------------------------- */
  const startGame = useCallback(
    () => startLevelInternal(currentLevelRef.current),
    [startLevelInternal]
  );

  const resetGame = useCallback(async () => {
    clearTimers();
    const cfg = levelConfig(startLevel);

    setCurrentLevelAndRef(startLevel);

    const resetStats: GameStats = {
      points: 0,
      attemptsLeft: cfg.attempts,
      missesLeft: cfg.maxMisses,
      level: startLevel,
    };

    setStats(resetStats);
    statsRef.current = resetStats;

    setTiles(buildTiles(cfg.gridSize));
    tilesRef.current = buildTiles(cfg.gridSize);

    setIsGameOver(false);
    setIsLevelComplete(false);
    setIsPreviewing(false);
    setIsRunning(false);

    await LocalProgressService.clearProgress();
    emit("reset", { level: startLevel });
  }, [clearTimers, emit]);

  const nextLevel = useCallback(() => {
    // advance only if not final already
    const next = currentLevelRef.current + 1;
    if (currentLevelRef.current >= maxLevel) {
      // already at final level, finish game
      setIsGameOver(true);
      emit("gameFinished", { stats: statsRef.current });
      return;
    }

    setCurrentLevelAndRef(next);
    setIsGameOver(false);
    setIsLevelComplete(false);
    startLevelInternal(next);
    setStats((s) => {
      const nextS = {
        ...s,
        ...{
          attemptsLeft: levelConfig(next).attempts,
          missesLeft: levelConfig(next).maxMisses,
        },
      };
      statsRef.current = nextS;
      return nextS;
    });
    emit("nextLevel", { next });
  }, [emit, maxLevel, startLevelInternal]);

  /* ------------------------- persistence: hydrate on mount, save on change ------------------------- */
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const loaded = await LocalProgressService.loadProgress();
        if (!mounted) return;

        if (loaded?.stats) {
          const l = loaded.stats.level ?? startLevel;
          const cfg = levelConfig(l);

          setCurrentLevelAndRef(l);
          setGridSize(cfg.gridSize);
          setTiles(buildTiles(cfg.gridSize));
          tilesRef.current = buildTiles(cfg.gridSize);

          const restored: GameStats = {
            points: loaded.stats.points ?? 0,
            level: l,
            attemptsLeft: loaded.stats.attemptsLeft ?? cfg.attempts,
            missesLeft: loaded.stats.missesLeft ?? cfg.maxMisses,
          };

          setStats(restored);
          statsRef.current = restored;
        }
      } finally {
        hasHydratedRef.current = true;
        emit("hydrated", { stats: statsRef.current });
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // save whenever stats change — but only after hydration completes
  useEffect(() => {
    if (!hasHydratedRef.current) return;
    LocalProgressService.saveProgress(stats);
  }, [stats]);

  return {
    tiles,
    path,
    gridSize,
    stats,
    isPreviewing,
    isRunning,
    isGameOver,
    isLevelComplete,
    areAllLevelsCompleted,
    clickTile,
    startGame,
    resetGame,
    nextLevel,
    setCurrentLevel: setCurrentLevelAndRef,
    // Return handlers only for win/lose operations developers may want to trigger manually
    handlers: {
      handleWin: () => {
        // manually trigger win for current state
        handleLevelCompleteInternal();
      },
      handleGameOver: () => {
        setIsGameOver(true);
        setIsRunning(false);
        opts?.onLose?.({
          stats: statsRef.current,
          level: currentLevelRef.current,
          gridSize,
        });
        emit("lose", { stats: statsRef.current });
      },
    },
    // event subsystem for observability & debugging
    events: { on, off, emit },
    // snapshot helper for external debug or tests
    _snapshot: {
      getStats: () => statsRef.current,
      getTiles: () => tilesRef.current,
      getPath: () => pathRef.current,
      getLevel: () => currentLevelRef.current,
    },
  };
}
